/**
 * CSV Categoriser Keywords — EDG-531
 *
 * Keyword-based heuristic for inferring category and statement type
 * from CSV source labels. Used when auto-creating custom line items
 * from unmatched CSV rows.
 *
 * Order matters: first match wins. More specific patterns must precede
 * general ones (e.g. 'bank fee' before 'fee', 'interest expense' before 'interest').
 */

export interface CategoryRule {
  match: string;
  category: string;
  statementType: 'pl' | 'bs';
}

export const CATEGORY_KEYWORDS: CategoryRule[] = [
  // Revenue (P&L) — specific before general
  { match: 'gross revenue', category: 'revenue', statementType: 'pl' },
  { match: 'net revenue', category: 'revenue', statementType: 'pl' },
  { match: 'revenue', category: 'revenue', statementType: 'pl' },
  { match: 'sales', category: 'revenue', statementType: 'pl' },
  { match: 'income', category: 'revenue', statementType: 'pl' },
  { match: 'commission', category: 'revenue', statementType: 'pl' },
  { match: 'fee income', category: 'revenue', statementType: 'pl' },
  { match: 'service charge', category: 'revenue', statementType: 'pl' },

  // Cost of Goods Sold (P&L)
  { match: 'cost of goods', category: 'cogs', statementType: 'pl' },
  { match: 'cost of sales', category: 'cogs', statementType: 'pl' },
  { match: 'materials', category: 'cogs', statementType: 'pl' },
  { match: 'freight', category: 'cogs', statementType: 'pl' },
  { match: 'direct labour', category: 'cogs', statementType: 'pl' },
  { match: 'manufacturing', category: 'cogs', statementType: 'pl' },
  { match: 'subcontract', category: 'cogs', statementType: 'pl' },
  { match: 'inventory adjust', category: 'cogs', statementType: 'pl' },

  // Personnel (P&L) — before general 'expense'
  { match: 'wages', category: 'personnel', statementType: 'pl' },
  { match: 'salar', category: 'personnel', statementType: 'pl' },
  { match: 'superannuation', category: 'personnel', statementType: 'pl' },
  { match: 'payroll', category: 'personnel', statementType: 'pl' },
  { match: 'workers comp', category: 'personnel', statementType: 'pl' },
  { match: 'staff', category: 'personnel', statementType: 'pl' },
  { match: 'employee', category: 'personnel', statementType: 'pl' },
  { match: 'recruitment', category: 'personnel', statementType: 'pl' },
  { match: 'training', category: 'personnel', statementType: 'pl' },
  { match: 'bonus', category: 'personnel', statementType: 'pl' },
  { match: 'contractor', category: 'personnel', statementType: 'pl' },

  // Non-Operating (P&L) — specific before general
  { match: 'depreciation', category: 'non_operating', statementType: 'pl' },
  { match: 'amortisation', category: 'non_operating', statementType: 'pl' },
  { match: 'interest expense', category: 'non_operating', statementType: 'pl' },
  { match: 'interest income', category: 'non_operating', statementType: 'pl' },
  { match: 'tax expense', category: 'non_operating', statementType: 'pl' },
  { match: 'gain on', category: 'non_operating', statementType: 'pl' },
  { match: 'loss on', category: 'non_operating', statementType: 'pl' },
  { match: 'foreign exchange', category: 'non_operating', statementType: 'pl' },

  // Operating Expenses (P&L) — general catch-all for P&L
  { match: 'rent', category: 'opex', statementType: 'pl' },
  { match: 'utilit', category: 'opex', statementType: 'pl' },
  { match: 'insurance', category: 'opex', statementType: 'pl' },
  { match: 'bank fee', category: 'opex', statementType: 'pl' },
  { match: 'bank charge', category: 'opex', statementType: 'pl' },
  { match: 'office', category: 'opex', statementType: 'pl' },
  { match: 'professional fee', category: 'opex', statementType: 'pl' },
  { match: 'accounting', category: 'opex', statementType: 'pl' },
  { match: 'legal', category: 'opex', statementType: 'pl' },
  { match: 'audit', category: 'opex', statementType: 'pl' },
  { match: 'consulting', category: 'opex', statementType: 'pl' },
  { match: 'advertising', category: 'opex', statementType: 'pl' },
  { match: 'marketing', category: 'opex', statementType: 'pl' },
  { match: 'travel', category: 'opex', statementType: 'pl' },
  { match: 'motor vehicle expense', category: 'opex', statementType: 'pl' },
  { match: 'fuel', category: 'opex', statementType: 'pl' },
  { match: 'repair', category: 'opex', statementType: 'pl' },
  { match: 'maintenance', category: 'opex', statementType: 'pl' },
  { match: 'cleaning', category: 'opex', statementType: 'pl' },
  { match: 'phone', category: 'opex', statementType: 'pl' },
  { match: 'internet', category: 'opex', statementType: 'pl' },
  { match: 'software', category: 'opex', statementType: 'pl' },
  { match: 'subscription', category: 'opex', statementType: 'pl' },
  { match: 'membership', category: 'opex', statementType: 'pl' },
  { match: 'licence', category: 'opex', statementType: 'pl' },
  { match: 'bad debt', category: 'opex', statementType: 'pl' },
  { match: 'donation', category: 'opex', statementType: 'pl' },
  { match: 'sundry', category: 'opex', statementType: 'pl' },

  // Current Assets (BS)
  { match: 'cash', category: 'current_asset', statementType: 'bs' },
  { match: 'receivable', category: 'current_asset', statementType: 'bs' },
  { match: 'inventory', category: 'current_asset', statementType: 'bs' },
  { match: 'prepayment', category: 'current_asset', statementType: 'bs' },
  { match: 'prepaid', category: 'current_asset', statementType: 'bs' },
  { match: 'deposit', category: 'current_asset', statementType: 'bs' },
  { match: 'work in progress', category: 'current_asset', statementType: 'bs' },
  { match: 'gst receivable', category: 'current_asset', statementType: 'bs' },

  // Non-Current Assets (BS)
  { match: 'property', category: 'non_current_asset', statementType: 'bs' },
  { match: 'plant and', category: 'non_current_asset', statementType: 'bs' },
  { match: 'equipment', category: 'non_current_asset', statementType: 'bs' },
  { match: 'machinery', category: 'non_current_asset', statementType: 'bs' },
  { match: 'furniture', category: 'non_current_asset', statementType: 'bs' },
  { match: 'motor vehicle', category: 'non_current_asset', statementType: 'bs' },
  { match: 'goodwill', category: 'non_current_asset', statementType: 'bs' },
  { match: 'intangible', category: 'non_current_asset', statementType: 'bs' },
  { match: 'right of use', category: 'non_current_asset', statementType: 'bs' },
  { match: 'investment', category: 'non_current_asset', statementType: 'bs' },
  { match: 'deferred tax asset', category: 'non_current_asset', statementType: 'bs' },

  // Current Liabilities (BS)
  { match: 'accounts payable', category: 'current_liability', statementType: 'bs' },
  { match: 'payable', category: 'current_liability', statementType: 'bs' },
  { match: 'short-term', category: 'current_liability', statementType: 'bs' },
  { match: 'short term', category: 'current_liability', statementType: 'bs' },
  { match: 'gst payable', category: 'current_liability', statementType: 'bs' },
  { match: 'payg', category: 'current_liability', statementType: 'bs' },
  { match: 'provision', category: 'current_liability', statementType: 'bs' },
  { match: 'accrued', category: 'current_liability', statementType: 'bs' },
  { match: 'tax payable', category: 'current_liability', statementType: 'bs' },

  // Non-Current Liabilities (BS)
  { match: 'long-term', category: 'non_current_liability', statementType: 'bs' },
  { match: 'long term', category: 'non_current_liability', statementType: 'bs' },
  { match: 'loan', category: 'non_current_liability', statementType: 'bs' },
  { match: 'mortgage', category: 'non_current_liability', statementType: 'bs' },
  { match: 'deferred tax liabilit', category: 'non_current_liability', statementType: 'bs' },
  { match: 'lease liabilit', category: 'non_current_liability', statementType: 'bs' },

  // Equity (BS)
  { match: 'capital', category: 'equity', statementType: 'bs' },
  { match: 'retained', category: 'equity', statementType: 'bs' },
  { match: 'reserve', category: 'equity', statementType: 'bs' },
  { match: 'drawing', category: 'equity', statementType: 'bs' },
  { match: 'equity', category: 'equity', statementType: 'bs' },
  { match: 'retained profit', category: 'equity', statementType: 'bs' },
  { match: 'accumulated profit', category: 'equity', statementType: 'bs' },
];

/**
 * Infer category and statement type from a CSV source label.
 * First-match wins. Falls back to Other Operating Expenses (P&L)
 * when no keyword matches — visible and overridable in the preview UI.
 */
export function inferCategory(label: string): { category: string; statementType: 'pl' | 'bs' } {
  const normalised = label.toLowerCase().trim();
  for (const rule of CATEGORY_KEYWORDS) {
    if (normalised.includes(rule.match)) {
      return { category: rule.category, statementType: rule.statementType };
    }
  }
  // Fallback: Other Operating Expenses (P&L)
  return { category: 'opex', statementType: 'pl' };
}
